import React, {
  useContext,
  useState,
  useEffect
} from "react";

import {
  Link
} from "react-router-dom";

import {
  motion,
  AnimatePresence
} from "framer-motion";

import {
  CartContext
} from "../context/CartContext";

import {
  WishlistContext
} from "../context/WishlistContext";

import CartDrawer from "./CartDrawer";

import SearchOverlay from "./SearchOverlay";

import {
  FaHeart,
  FaShoppingBag,
  FaUser,
  FaSearch
} from "react-icons/fa";

import {
  searchProducts
} from "../services/productService";

function Navbar({
  darkMode,
  setDarkMode
}) {

  const { cartItems } =
    useContext(CartContext);

  const { wishlist } =
    useContext(WishlistContext);

  const [openCart, setOpenCart] =
    useState(false);

  const [searchOpen, setSearchOpen] =
    useState(false);

  const [allProducts, setAllProducts] =
    useState([]);

  const [announcementIndex,
    setAnnouncementIndex] =
    useState(0);

  const [scrolled, setScrolled] =
    useState(false);

  const token =
    localStorage.getItem("token");

  /* ===============================
     ANNOUNCEMENTS
  =============================== */

  const announcements = [

    "✨ Handcrafted Sacred Luxury Art",

    "🚚 Free Shipping Across India",

    "🔒 Secure Checkout & Easy Returns",

    "🎁 Premium Spiritual Gifting Collection"

  ];

  /* ===============================
     AUTO SLIDE
  =============================== */

  useEffect(() => {

    const interval = setInterval(() => {

      setAnnouncementIndex((prev) =>
        prev === announcements.length - 1
          ? 0
          : prev + 1
      );

    }, 3500);

    return () => clearInterval(interval);

  }, []);

  /* ===============================
     SCROLL EFFECT
  =============================== */

  useEffect(() => {

    const handleScroll = () => {

      if (window.scrollY > 20) {

        setScrolled(true);

      } else {

        setScrolled(false);

      }

    };

    window.addEventListener(
      "scroll",
      handleScroll
    );

    return () =>
      window.removeEventListener(
        "scroll",
        handleScroll
      );

  }, []);

  /* ===============================
     LOAD PRODUCTS
  =============================== */

  useEffect(() => {

    const fetchProducts =
      async () => {

      try {

        const data =
          await searchProducts();

        setAllProducts(
          Array.isArray(data)
            ? data
            : []
        );

      } catch (err) {

        console.error(err);

      }

    };

    fetchProducts();

  }, []);

  return (

    <>

      {/* ===============================
          ANNOUNCEMENT BAR
      =============================== */}

      <div className="
        bg-[#111111]
        text-white
        overflow-hidden
        h-10
        flex items-center
        justify-center
        text-[10px]
        uppercase
        tracking-[0.35em]
        relative
        z-[60]
        border-b border-white/10
      ">

        <AnimatePresence mode="wait">

          <motion.div
            key={announcementIndex}
            initial={{
              opacity: 0,
              y: 10
            }}
            animate={{
              opacity: 1,
              y: 0
            }}
            exit={{
              opacity: 0,
              y: -10
            }}
            transition={{
              duration: 0.45
            }}
            className="absolute"
          >

            {announcements[announcementIndex]}

          </motion.div>

        </AnimatePresence>

      </div>

      {/* ===============================
          PREMIUM NAVBAR
      =============================== */}

      <motion.nav

        initial={{
          y: -40,
          opacity: 0
        }}

        animate={{
          y: 0,
          opacity: 1
        }}

        transition={{
          duration: 0.6
        }}

        className={`
          sticky top-0 z-50
          transition-all duration-500
          border-b

          ${
            scrolled
              ? `
                bg-white/75
                backdrop-blur-2xl
                shadow-[0_8px_40px_rgba(0,0,0,0.08)]
                border-stone-200/70
                py-3
              `
              : `
                bg-white/55
                backdrop-blur-xl
                border-stone-100
                py-5
              `
          }
        `}
      >

        <div className="
          max-w-7xl mx-auto
          px-4 md:px-8
          flex items-center justify-between
          gap-6
        ">

          {/* ===============================
              BRAND
          =============================== */}

          <Link
            to="/"
            className="
              flex flex-col
              leading-none
              group
              min-w-fit
            "
          >

            <motion.h1

              whileHover={{
                scale: 1.03
              }}

              transition={{
                duration: 0.3
              }}

              className="
                text-3xl md:text-[42px]
                font-serif
                italic
                tracking-wide
                text-stone-900
                transition-all duration-300
              "
            >

              isabi

            </motion.h1>

            <span className="
              text-[9px]
              uppercase
              tracking-[0.45em]
              text-stone-400
              mt-1 ml-1
            ">

              studio

            </span>

          </Link>

          {/* ===============================
              SEARCH BAR
          =============================== */}

          <motion.button

            whileHover={{
              scale: 1.01
            }}

            whileTap={{
              scale: 0.98
            }}

            onClick={() =>
              setSearchOpen(true)
            }

            className="
              hidden md:flex
              items-center
              w-[460px]
              max-w-full

              px-6 py-3.5

              rounded-full

              bg-white/80
              backdrop-blur-xl

              border border-white/60

              shadow-[0_4px_20px_rgba(0,0,0,0.06)]

              hover:shadow-[0_8px_30px_rgba(0,0,0,0.10)]
              hover:bg-white

              transition-all duration-500

              text-sm text-stone-400
            "
          >

            <FaSearch
              className="
                mr-4
                text-stone-400
              "
            />

            <span className="
              font-light
              tracking-wide
            ">

              Search sacred art...

            </span>

          </motion.button>

          {/* ===============================
              ACTIONS
          =============================== */}

          <div className="
            flex items-center
            gap-5 md:gap-6
            text-stone-800
          ">

            {/* ACCOUNT */}

            <motion.div
              whileHover={{
                y: -2,
                scale: 1.08
              }}
            >

              <Link
                to="/account"
                className="
                  hover:text-black
                  transition-all duration-300
                "
              >

                <FaUser size={18} />

              </Link>

            </motion.div>

            {/* WISHLIST */}

            <motion.div
              whileHover={{
                y: -2,
                scale: 1.08
              }}
              className="relative"
            >

              <Link
                to="/wishlist"
                className="
                  relative
                  hover:text-black
                  transition-all duration-300
                "
              >

                <FaHeart size={18} />

                {wishlist.length > 0 && (

                  <motion.span

                    initial={{
                      scale: 0
                    }}

                    animate={{
                      scale: 1
                    }}

                    className="
                      absolute
                      -top-2 -right-2

                      bg-[#B91C1C]
                      text-white

                      text-[10px]

                      w-5 h-5

                      flex items-center justify-center

                      rounded-full

                      shadow-md
                    "
                  >

                    {wishlist.length}

                  </motion.span>

                )}

              </Link>

            </motion.div>

            {/* CART */}

            <motion.button

              whileHover={{
                y: -2,
                scale: 1.08
              }}

              onClick={() =>
                setOpenCart(true)
              }

              className="
                relative
                hover:text-black
                transition-all duration-300
              "
            >

              <FaShoppingBag size={18} />

              {cartItems.length > 0 && (

                <motion.span

                  initial={{
                    scale: 0
                  }}

                  animate={{
                    scale: 1
                  }}

                  className="
                    absolute
                    -top-2 -right-2

                    bg-black
                    text-white

                    text-[10px]

                    w-5 h-5

                    flex items-center justify-center

                    rounded-full

                    shadow-md
                  "
                >

                  {cartItems.length}

                </motion.span>

              )}

            </motion.button>

            {/* DARK MODE */}

            <motion.button

              whileHover={{
                scale: 1.05
              }}

              whileTap={{
                scale: 0.95
              }}

              onClick={() =>
                setDarkMode(!darkMode)
              }

              className="
                border border-stone-200

                bg-white/70
                backdrop-blur-xl

                px-3 py-1.5

                rounded-full

                text-xs

                shadow-sm

                hover:bg-white
                hover:shadow-md

                transition-all duration-300
              "
            >

              {darkMode ? "☀" : "🌙"}

            </motion.button>

          </div>

        </div>

      </motion.nav>

      {/* ===============================
          SEARCH OVERLAY
      =============================== */}

      <SearchOverlay
        open={searchOpen}
        onClose={() =>
          setSearchOpen(false)
        }
        products={allProducts}
      />

      {/* ===============================
          CART DRAWER
      =============================== */}

      <CartDrawer
        open={openCart}
        onClose={() =>
          setOpenCart(false)
        }
      />

    </>

  );

}

export default Navbar;